import { formateTableDetails } from '@fmc-digital/marketing';
import { useQuery } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import { ApiLogger, logAPIEvent } from 'fmc-digital-common';
import { v4 } from 'uuid';
import { auth, config } from '../../../config';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useLoanSummaryQuery = (enabledLoanSummary: boolean, applicationId: string) => {
    const query = useQuery({
        queryKey: ['loanSummary', applicationId, enabledLoanSummary],
        queryFn: async () =>
            ApiLogger(
                {
                    method: 'get',
                    url: `${config.env('loanUrl')}/summary/${applicationId}`,
                    headers: {
                        'x-correlation-id': v4().toString(),
                        Authorization: `Bearer ${auth.getOkta().getAccessToken()}`,
                        'x-api-key': config.env('xApiKey'),
                        source: 'DigiApp'
                    },
                    timeout: 300000
                } as AxiosRequestConfig,
                logAPIEvent
            ),
        enabled: enabledLoanSummary,
        retry: 20,
        retryDelay: 30000,
        select: data => {
            const bodyDetails = data.bodyDetails;
            const monthlyPayment = buildMonthlyPaymentData(bodyDetails);
            const tol = buildSupportingTables(bodyDetails, 'Terms of the loan', 'termsOfLoan');
            const ctc = buildSupportingTables(bodyDetails, 'Calculating Cash to Close', 'cashToClose');
            return {
                tol,
                ctc,
                monthlyPayment,
                headerDetails: data.headerDetails
            };
        }
    });
    return query;
};
type TableDetails = {
    headers: string[];
    heading: string;
    header?: string;
    rows: TableDetails[];
};
const buildSupportingTables = (bodyDetails: any, tableHeader: string, tableKey: string) => {
    const loanConditions: TableDetails | any = bodyDetails?.find((table: any) => {
        if (table?.key) {
            return table.key === tableKey;
        } else {
            return table?.header === tableHeader || table?.heading === tableHeader;
        }
    });
    const newData = loanConditions?.rows.map((item: any) => {
        return {
            name: item?.name,
            currentLoan: item?.currentLoan,
            newLoan: item?.newLoan,
            key: item?.key,
            type: item?.type,
            additionalInfo: item?.additionalInfo?.[0]
        };
    });

    let isNotZero = false;
    if (tableKey === 'cashToClose') {
        newData?.forEach((item: any) => {
            if (item.key === 'estimatedLoanCredits' && item.newLoan > 0) {
                isNotZero = true;
            }
            return delete item.currentLoan;
        });
    }
    const formattedTable = newData ? formateTableDetails(newData) : [];
    if (tableKey === 'cashToClose') {
        formattedTable?.forEach((item: any) => {
            if (item.key === 'estimatedLoanCredits' && isNotZero) {
                item.newLoan = `-${item.newLoan}`;
            }
        });
    }
    formattedTable?.forEach((item: any) => {
        return delete item.key;
    });
    return {
        heading: loanConditions?.header || loanConditions?.heading || '',
        headers: [...loanConditions?.headers, ''],
        rows: formattedTable
    };
};
const buildMonthlyPaymentData = (bodyDetails: any) => {
    const monthlyPaymentData = bodyDetails.find((item: any) => {
        if (item?.key) {
            return item.key === 'monthlyPayment';
        } else {
            return item?.heading === 'Monthly payment';
        }
    });
    const rows = monthlyPaymentData?.rows.map((row: any) => {
        if (row.key === 'totalPayment') {
            if (row?.newLoan < row?.currentLoan) {
                return { ...row, arrow: true, drawerArrow: '' };
            }
            return { ...row, arrow: false, drawerArrow: '' };
        } else {
            const info: any = row?.additionalInfo;
            const subRows: any = row?.rows;
            const drawerArrow = info?.length > 0 || subRows?.length > 0 ? 'drawerArrow' : '';
            return { ...row, arrow: false, drawerArrow };
        }
    });
    return {
        heading: monthlyPaymentData?.heading || '',
        headers: monthlyPaymentData?.headers || [],
        rows: rows || []
    };
};
